import React, { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { translate } from '../../data/translations';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import useStore from '@data/state/zustand';
import { getDaysBetween } from '@pages/generalLedger/glCards/accountCards';
import { DayPickerLocale } from '@pages/newReservations/reservations/DateTimeSelector';

interface Props {
    onChange?: (type: 'from' | 'to', date: Date) => void;
    initialFrom?: Date;
    initialTo?: Date;
    fromYear?: number;
    toYear?: number;
    removeDateRange?: boolean;
    disableBefore?: Date;
}

export default function RangePickerDual({
    onChange,
    initialFrom,
    initialTo,
    fromYear,
    toYear,
    removeDateRange,
    disableBefore,
}: Props) {
    const [range, setRange] = useState<{ from: Date; to: Date }>({
        from: initialFrom
            ? initialFrom
            : (new Date(new Date().getFullYear(), new Date().getMonth() - 1, new Date().getDate()) as Date),
        to: initialTo ? initialTo : new Date(),
    });
    const [activePicker, setActivePicker] = useState<'from' | 'to' | null>(null);
    const otherSettings = useStore.getState().otherSettings;
    const locale = useStore.getState().locale;

    const pickerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
                setActivePicker(null);
            }
        };

        if (activePicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activePicker]);

    const handleSelect = (date?: Date) => {
        if (!date || !activePicker) return;

        const newRange =
            activePicker === 'from'
                ? { ...range, from: new Date(date.setHours(0, 0, 0, 0)) }
                : { ...range, to: new Date(date.setHours(23, 59, 59, 999)) };

        const daysBetween = removeDateRange ? true : getDaysBetween(newRange.from?.getTime(), newRange.to?.getTime());
        if (daysBetween === true || daysBetween < 40) {
            setRange(newRange);
            onChange?.(activePicker, date);
            setActivePicker(null);
        } else {
            toast(translate('Maximum date range is 40 days'), { type: toast.TYPE.ERROR });
        }
    };

    return (
        <div className="range-picker-container" ref={pickerRef} style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            {/* FROM INPUT */}
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    placeholder={translate('From')}
                    readOnly
                    value={
                        range.from
                            ? dayjs(range.from).format(
                                  otherSettings?.dateFormat ? otherSettings.dateFormat : 'DD/MM/YYYY'
                              )
                            : ''
                    }
                    onClick={() => setActivePicker('from')}
                    className="date-picker-input-custom"
                />
                {activePicker === 'from' && (
                    <div className="absolute z-10 bg-white shadow p-2">
                        <DayPicker
                            mode="single"
                            selected={range.from}
                            onSelect={handleSelect}
                            locale={DayPickerLocale[locale] ? DayPickerLocale[locale] : undefined}
                            captionLayout={fromYear && toYear ? 'dropdown-buttons' : undefined}
                            fromYear={fromYear ? fromYear : undefined}
                            toYear={toYear ? toYear : undefined}
                            defaultMonth={range.from}
                            numberOfMonths={1}
                            weekStartsOn={1}
                            disabled={{ before: disableBefore, after: range.to }}
                            modifiers={{
                                range: { from: range.from, to: range.to },
                                start: range.from,
                                end: range.to,
                            }}
                            modifiersClassNames={{
                                start: 'custom-selected',
                                end: 'custom-selected',
                                range: 'custom-range',
                                today: 'custom-today',
                            }}
                        />
                    </div>
                )}
            </div>

            <span>-</span>

            {/* TO INPUT */}
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    placeholder={translate('To')}
                    readOnly
                    value={
                        range.to
                            ? dayjs(range.to).format(
                                  otherSettings?.dateFormat ? otherSettings.dateFormat : 'DD/MM/YYYY'
                              )
                            : ''
                    }
                    onClick={() => setActivePicker('to')}
                    className="date-picker-input-custom"
                />
                {activePicker === 'to' && (
                    <div className="absolute z-10 bg-white shadow p-2">
                        <DayPicker
                            mode="single"
                            selected={range.to}
                            onSelect={handleSelect}
                            locale={DayPickerLocale[locale] ? DayPickerLocale[locale] : undefined}
                            captionLayout={fromYear && toYear ? 'dropdown-buttons' : undefined}
                            fromYear={fromYear ? fromYear : undefined}
                            toYear={toYear ? toYear : undefined}
                            defaultMonth={range.to}
                            numberOfMonths={1}
                            weekStartsOn={1}
                            disabled={{ before: range.from }}
                            modifiers={{
                                range: { from: range.from, to: range.to },
                                start: range.from,
                                end: range.to,
                            }}
                            modifiersClassNames={{
                                start: 'custom-selected',
                                end: 'custom-selected',
                                range: 'custom-range',
                                today: 'custom-today',
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
